import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { buildTileUrl, tileAttribution as buildAttribution } from '../lib/tiles';
import AppSwitcher from '../components/AppSwitcher';
import GoogleOneTap from '../components/auth/GoogleOneTap';
import {
  Box,
  ArrowRight,
  Sparkles,
  Building2,
  Zap,
  Users,
  KeyRound,
  Sun,
  Moon,
} from 'lucide-react';

import '@fontsource/manrope/400.css';
import '@fontsource/manrope/500.css';
import '@fontsource/manrope/700.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const THEME_KEY = 'shoebox_theme';

const MAP_CENTER = [47.5, 4.5];
const MAP_ZOOM = 5;

// --- Pin icons (theme-aware via outline) ---
const makePin = (color, ring) => new L.DivIcon({
  className: '',
  html: `<div style="
    width:14px;height:14px;background:${color};border-radius:50%;
    border:2px solid ${ring};box-shadow:0 0 18px ${color}b3;
    animation:pulseGold 2.4s infinite ease-in-out;
  "></div>`,
  iconSize: [14, 14],
  iconAnchor: [7, 7],
});

// --- Slow continental drift to make the map feel alive ---
const SlowDrifter = () => {
  const map = useMap();
  useEffect(() => {
    let cancelled = false;
    const targets = [
      [48.8566, 2.3522, 5],
      [45.7640, 4.8357, 5],
      [50.8503, 4.3517, 5],
      [46.2044, 6.1432, 5],
      [43.6047, 1.4442, 5],
    ];
    let i = 0;
    const drift = () => {
      if (cancelled) return;
      const [lat, lng, z] = targets[i % targets.length];
      map.flyTo([lat, lng], z, { duration: 8, easeLinearity: 0.2 });
      i += 1;
    };
    drift();
    const id = setInterval(drift, 12000);
    return () => { cancelled = true; clearInterval(id); };
  }, [map]);
  return null;
};

// =====================================================================
// Theme tokens
// =====================================================================
const themes = {
  dark: {
    bg: '#09090B',
    text: '#FFFFFF',
    textMuted: 'rgba(255,255,255,0.55)',
    textDim: 'rgba(255,255,255,0.35)',
    accent: '#D9A05B',
    accentBg: 'rgba(217,160,91,0.15)',
    cardBg: 'rgba(0,0,0,0.30)',
    cardBorder: 'rgba(255,255,255,0.10)',
    cardBlur: '24px',
    pinRing: '#09090B',
    pinPub: '#10B981',
    overlayGradient:
      'radial-gradient(ellipse at center, rgba(9,9,11,0.10) 0%, rgba(9,9,11,0.55) 55%, rgba(9,9,11,0.92) 100%)',
    legendBg: 'rgba(0,0,0,0.50)',
  },
  smooth: {
    bg: '#F7F4EE',
    text: '#1A1A1A',
    textMuted: 'rgba(26,26,26,0.65)',
    textDim: 'rgba(26,26,26,0.40)',
    accent: '#B8894A',
    accentBg: 'rgba(184,137,74,0.12)',
    cardBg: 'rgba(255,255,255,0.55)',
    cardBorder: 'rgba(26,26,26,0.10)',
    cardBlur: '20px',
    pinRing: '#F7F4EE',
    pinPub: '#0F8C68',
    overlayGradient:
      'radial-gradient(ellipse at center, rgba(247,244,238,0.05) 0%, rgba(247,244,238,0.35) 60%, rgba(247,244,238,0.75) 100%)',
    legendBg: 'rgba(255,255,255,0.65)',
  },
};

// =====================================================================
// Page
// =====================================================================
const LandingPage = () => {
  const navigate = useNavigate();
  const { user, updateTheme } = useAuth();
  const [featured, setFeatured] = useState([]);
  const [theme, setTheme] = useState(() => {
    try { return localStorage.getItem(THEME_KEY) || 'dark'; } catch { return 'dark'; }
  });
  const heroRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/public/featured-projects`)
      .then(r => setFeatured(r.data || []))
      .catch(() => setFeatured([]));
  }, []);

  // Hydrate theme from user when available
  useEffect(() => {
    if (user?.theme && (user.theme === 'dark' || user.theme === 'smooth') && user.theme !== theme) {
      setTheme(user.theme);
      try { localStorage.setItem(THEME_KEY, user.theme); } catch {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.theme]);

  const t = themes[theme];

  const toggleTheme = () => {
    const next = theme === 'dark' ? 'smooth' : 'dark';
    setTheme(next);
    try { localStorage.setItem(THEME_KEY, next); } catch {}
    if (updateTheme) updateTheme(next);
  };

  const handleLogin = () => {
    // Ouvre la modale glassmorphism par-dessus la landing (sans changer de route)
    // pour limiter le sentiment de changement d'interface.
    const next = encodeURIComponent('/studio/dashboard');
    navigate(`/?auth=1&next=${next}`, { replace: false });
  };

  // ==== Theme-aware tile URLs ====
  // Single-source-of-truth in /lib/tiles.js. Mapbox satellite-streets-v12 (dark)
  // + light-v11 (smooth) when REACT_APP_MAPBOX_TOKEN is set; else Esri+Carto fallback.
  const darkTileUrl  = buildTileUrl('dark');
  const lightTileUrl = buildTileUrl('smooth');
  const tileAttribution = buildAttribution(theme);

  const goldPin = makePin(t.accent, t.pinRing);
  const pubPin = makePin(t.pinPub, t.pinRing);

  return (
    <div
      className="relative w-full h-screen overflow-hidden"
      style={{ background: t.bg, color: t.text, fontFamily: 'Manrope, sans-serif' }}
      data-testid="landing-page"
      data-theme={theme}
    >
      {/* One-Tap silent login (renders only when user logged-out + direct flow enabled) */}
      <GoogleOneTap next="/studio/dashboard" />
      {/* Theme styles + smooth tile crossfade */}
      <style>{`
        @keyframes pulseGold {
          0%,100% { transform: scale(1); opacity: 1; }
          50%     { transform: scale(1.4); opacity: .55; }
        }
        @keyframes floatY {
          0%,100% { transform: translateY(0); }
          50%     { transform: translateY(-4px); }
        }
        .leaflet-container { background: ${t.bg} !important; transition: background 700ms ease; }
        /* Smooth crossfade between the two stacked tile layers when toggling theme */
        .leaflet-pane .leaflet-layer { transition: opacity 700ms ease-in-out; }
        .leaflet-control-attribution {
          background: ${theme === 'smooth' ? 'rgba(255,255,255,.6)' : 'rgba(0,0,0,.4)'} !important;
          color: ${t.textDim} !important;
          font-size: 9px !important;
          transition: background 700ms ease, color 700ms ease;
        }
        .leaflet-control-attribution a { color: ${t.accent} !important; }
      `}</style>

      {/* ==== Map background ==== */}
      <div className="absolute inset-0 z-0">
        <MapContainer
          ref={heroRef}
          center={MAP_CENTER}
          zoom={MAP_ZOOM}
          zoomControl={false}
          scrollWheelZoom={false}
          doubleClickZoom={false}
          dragging={false}
          touchZoom={false}
          keyboard={false}
          attributionControl={true}
          className="w-full h-full"
          style={{ background: t.bg }}
        >
          {/* Dark tile (always mounted, opacity-faded) */}
          <TileLayer
            url={darkTileUrl}
            attribution={tileAttribution}
            opacity={theme === 'smooth' ? 0 : 1}
            zIndex={1}
          />
          {/* Light tile (always mounted, opacity-faded) */}
          <TileLayer
            url={lightTileUrl}
            attribution={tileAttribution}
            opacity={theme === 'smooth' ? 1 : 0}
            zIndex={2}
          />
          <SlowDrifter />
          {featured.map((p) =>
            Array.isArray(p.coords) && p.coords.length === 2 ? (
              <Marker
                key={p.project_id}
                position={p.coords}
                icon={p.status === 'published' ? pubPin : goldPin}
              />
            ) : null
          )}
        </MapContainer>
      </div>

      {/* Vignette */}
      <div className="pointer-events-none absolute inset-0 z-10" style={{ background: t.overlayGradient }} />

      {/* ==== Top nav ==== */}
      <nav className="absolute top-0 left-0 right-0 z-30 flex items-center justify-between px-4 sm:px-8 py-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center"
               style={{ background: t.accent, boxShadow: `0 0 22px ${t.accent}80` }}>
            <Box className="w-4 h-4" style={{ color: theme === 'smooth' ? '#FFFFFF' : '#000000' }} />
          </div>
          <div className="flex flex-col leading-none">
            <span className="text-lg font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Shoebox<span style={{ color: t.accent }}>3D</span>
            </span>
            <span className="text-[9px] uppercase tracking-[0.22em] font-bold mt-1" style={{ color: t.textDim }}>
              A <span style={{ color: t.accent }}>Peter's Universe</span> Module
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Peter's Universe — App Switcher */}
          <AppSwitcher theme={theme} testid="landing-app-switcher" />
          {/* Theme toggle */}
          <button
            onClick={toggleTheme}
            data-testid="theme-toggle"
            className="w-9 h-9 rounded-lg flex items-center justify-center transition-all"
            style={{
              background: t.cardBg,
              border: `1px solid ${t.cardBorder}`,
              backdropFilter: `blur(${t.cardBlur})`,
              color: t.textMuted,
            }}
            title={theme === 'dark' ? 'Mode Smooth' : 'Mode Dark'}
            aria-label="Changer de thème"
          >
            {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
          <Button
            onClick={handleLogin}
            data-testid="nav-login-btn"
            className="font-medium h-9 px-4 rounded-lg"
            style={{
              background: t.accent,
              color: theme === 'smooth' ? '#FFFFFF' : '#000000',
              boxShadow: `0 0 18px ${t.accent}59`,
            }}
          >
            Connexion
          </Button>
        </div>
      </nav>

      {/* ==== Hero glass card ==== */}
      <div className="relative z-20 h-full flex items-center px-4 sm:px-10 lg:px-20">
        <div
          className="max-w-xl w-full rounded-3xl p-6 sm:p-8 shadow-2xl"
          style={{
            background: t.cardBg,
            border: `1px solid ${t.cardBorder}`,
            backdropFilter: `blur(${t.cardBlur})`,
            WebkitBackdropFilter: `blur(${t.cardBlur})`,
            animation: 'floatY 6s ease-in-out infinite',
          }}
          data-testid="landing-hero-card"
        >
          {/* Discreet badge: restricted community */}
          <div
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.18em] mb-4"
            style={{ background: t.accentBg, color: t.accent, border: `1px solid ${t.accent}40` }}
          >
            <KeyRound className="w-3 h-3" />
            Communauté privée · Sur invitation
          </div>

          <h1
            className="text-3xl sm:text-4xl lg:text-5xl font-semibold leading-[1.05] mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Vos projets immobiliers,<br />
            <span style={{ color: t.accent }}>cartographiés en 3D.</span>
          </h1>

          <p className="text-sm sm:text-base mb-5 leading-relaxed" style={{ color: t.textMuted }}>
            Une carte vivante, un cockpit unique. Importez vos plans, géolocalisez vos lots, générez des shoeboxes IA en quelques secondes.
          </p>

          {/* "Pourquoi" — 3 inline pills (replaces the scroll-down section) */}
          <div className="grid grid-cols-3 gap-2 mb-5">
            {[
              { icon: Zap, label: 'IA en secondes' },
              { icon: Building2, label: 'Vue projet 360°' },
              { icon: Users, label: 'Workflow B2B' },
            ].map((it, idx) => (
              <div
                key={idx}
                className="flex flex-col items-center text-center gap-1 py-2.5 rounded-xl"
                style={{
                  background: theme === 'smooth' ? 'rgba(255,255,255,0.45)' : 'rgba(255,255,255,0.04)',
                  border: `1px solid ${t.cardBorder}`,
                }}
                data-testid={`feature-pill-${idx}`}
              >
                <it.icon className="w-3.5 h-3.5" style={{ color: t.accent }} />
                <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: t.textMuted }}>
                  {it.label}
                </span>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="flex flex-col sm:flex-row gap-2.5">
            <Button
              onClick={handleLogin}
              data-testid="hero-cta-btn"
              size="lg"
              className="flex-1 font-medium h-11 px-5 text-sm rounded-xl"
              style={{
                background: t.accent,
                color: theme === 'smooth' ? '#FFFFFF' : '#000000',
                boxShadow: `0 0 22px ${t.accent}73`,
              }}
            >
              Accéder au Hub
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <button
              onClick={() => window.open('mailto:hello@shoebox3d.com?subject=Demande%20d%27acc%C3%A8s', '_self')}
              data-testid="request-access-btn"
              className="h-11 px-5 text-sm rounded-xl font-medium transition-all"
              style={{
                background: 'transparent',
                border: `1px solid ${t.cardBorder}`,
                color: t.textMuted,
                backdropFilter: 'blur(10px)',
              }}
            >
              Demander un accès
            </button>
          </div>

          {/* Tiny stats row */}
          <div className="flex items-center justify-between gap-4 mt-5 pt-4" style={{ borderTop: `1px solid ${t.cardBorder}` }}>
            <div>
              <p className="text-lg font-semibold" style={{ color: t.text, fontFamily: 'Outfit, sans-serif' }}>{featured.length || 5}</p>
              <p className="text-[9px] uppercase tracking-widest" style={{ color: t.textDim }}>Projets vitrine</p>
            </div>
            <div>
              <p className="text-lg font-semibold" style={{ color: t.text, fontFamily: 'Outfit, sans-serif' }}>98%</p>
              <p className="text-[9px] uppercase tracking-widest" style={{ color: t.textDim }}>Satisfaction</p>
            </div>
            <div>
              <p className="text-lg font-semibold" style={{ color: t.text, fontFamily: 'Outfit, sans-serif' }}>24h</p>
              <p className="text-[9px] uppercase tracking-widest" style={{ color: t.textDim }}>Délai moyen</p>
            </div>
            <div className="hidden sm:block">
              <p className="text-lg font-semibold flex items-center gap-1" style={{ color: t.accent, fontFamily: 'Outfit, sans-serif' }}>
                <Sparkles className="w-3.5 h-3.5" />
                IA
              </p>
              <p className="text-[9px] uppercase tracking-widest" style={{ color: t.textDim }}>Nano Banana</p>
            </div>
          </div>
        </div>
      </div>

      {/* Floating live legend (top-right) */}
      <div
        className="hidden md:flex absolute top-20 right-6 z-20 flex-col gap-1.5 px-3 py-2.5 rounded-xl text-xs"
        style={{
          background: t.legendBg,
          border: `1px solid ${t.cardBorder}`,
          backdropFilter: `blur(${t.cardBlur})`,
          color: t.textMuted,
        }}
      >
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: t.accent, boxShadow: `0 0 8px ${t.accent}b3` }} />
          En cours
        </div>
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: t.pinPub, boxShadow: `0 0 8px ${t.pinPub}99` }} />
          Publiés
        </div>
        <div className="flex items-center gap-2 text-[10px] pt-1" style={{ color: t.textDim, borderTop: `1px solid ${t.cardBorder}` }}>
          {featured.length} pins en direct
        </div>
      </div>

      {/* Tiny footer — bottom-center, ultra-discreet */}
      <div
        className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 text-[10px] tracking-widest uppercase"
        style={{ color: t.textDim }}
        data-testid="landing-footer"
      >
        © 2026 Shoebox3D · Hub Cartographique
      </div>
    </div>
  );
};

export default LandingPage;
